import angular from 'angular';
import csmCommonWeb from 'csm-common-web';
import ngAnimate from 'angular-animate';
// 'ui.grid.edit' and 'ui.grid.selection' modules are imported with uiGrid when it runs;
import booksSvc from '../../util/BooksSvc/BooksSvc';

import addInvoiceDialogTemplate from './AddInvoiceDialog.template.html';

export default angular
  .module('books.dialogs.AddInvoiceDialog', [
    csmCommonWeb,
    'ui.bootstrap',
    ngAnimate,
    'ui.grid',
    'ui.grid.edit',
    'ui.grid.selection',
    booksSvc
  ])
  .service('AddInvoiceDialog', addInvoiceDialog).name;

/*****************************************************************************
 * Service: AddInvoiceDialog
 *
 * Utility to display a dialog to create a new invoice, with existing POs and/or
 * new items. This dialog makes use of the invoiceVendor and invoiceItems
 * directives. When the new invoice has been successfully been created, it is
 * returned and the dialog is closed.
 *****************************************************************************/
/* @ngInject */
function addInvoiceDialog($uibModal, BooksSvc, BusyScreen) {
  /** Dialog Controller. */
  var AddInvoiceController = function (
    $scope,
    $uibModalInstance,
    Alert,
    billItems,
    InvoiceService,
    memo,
    restrictVendors,
    Tools,
    vendor
  ) {
    /*-----------------------------------------*/
    /* Object to hold saved invoice properties */
    /*-----------------------------------------*/
    $scope.invoice = {};

    /*-----------------*/
    /* Selected vendor */
    /*-----------------*/
    $scope.invoice.vendor = vendor;

    /*------------------------------------------*/
    /* Lock From input field if vendor provided */
    /*------------------------------------------*/
    if ($scope.invoice.vendor) $scope.lock = true;

    /*-----------------------------------------*/
    /* Populate items with imported bill items */
    /*-----------------------------------------*/
    $scope.invoice.items = billItems;

    /*----------*/
    /* Set memo */
    /*----------*/
    $scope.invoice.memo = memo;

    /*-------------------------------------------*/
    /* If a vendor is provided, set the vendorID */
    /*-------------------------------------------*/
    $scope.invoice.vendorID = vendor ? vendor.entity.id : null;
    $scope.invoice.entryDate = new Date();

    /*---------------------------------------------------*/
    /* Holds the index of the currently active tab in    */
    /* the dialog. Set the first tab (Vendor) as active. */
    /*---------------------------------------------------*/
    $scope.activeTab = 0;

    /*---------------------------------*/
    /* Set the restrict entities flag. */
    /*---------------------------------*/
    $scope.restrictVendors = restrictVendors;

    /*-------------------*/
    /* Validation object */
    /*-------------------*/
    $scope.valGroup = {};

    /***************************************************************************
     * dismiss */
    /**
     * Called when the close button is clicked. Clears any alerts and dismisses
     * the modal dialog.
     ***************************************************************************/
    $scope.dismiss = function () {
      Alert.clear();
      $uibModalInstance.dismiss();
    };

    /***************************************************************************
     * saveInvoice */
    /**
     * Called when the save button is clicked. Saves the invoice.
     ***************************************************************************/
    $scope.saveInvoice = function () {
      /*----------------------------------------------------*/
      /* Clear any previous alerts and reset the validator. */
      /*----------------------------------------------------*/
      Alert.clear();
      $scope.valGroup.reset();

      /*------------------------------------------------------------------*/
      /* An bill needs to have items. Focus on items tab if none entered. */
      /*------------------------------------------------------------------*/
      if (!InvoiceService.checkItems($scope.invoice.items)) {
        $scope.activeTab = 1;
        return;
      }

      /*---------------------------------------------*/
      /* Abort save if any product items are missing */
      /* account info and set focus on items tab.    */
      /*---------------------------------------------*/
      if (!InvoiceService.checkItemsAccount($scope.invoice.items)) {
        $scope.activeTab = 1;
        return;
      }

      /*-----------------------------------*/
      /* Abort save if vendor validation   */
      /* fails and set focus on Vendor tab */
      /*-----------------------------------*/
      if (!$scope.valGroup.validate()) {
        $scope.activeTab = 0;
        return;
      }

      /*-----------------------*/
      /* Check invoice amount. */
      /*-----------------------*/
      if (InvoiceService.getItemsTotal($scope.invoice.items) <= 0) {
        Alert.error('Invalid invoice amount.', 'Save Error');
        return;
      }

      /*------------------------------------*/
      /* Alert user to any overdrawn items. */
      /*------------------------------------*/
      InvoiceService.checkForOverdrawnAccounts($scope.invoice.items).then(
        function () {
          /*-----------------------------------------------------------------*/
          /* Modal resolved. Make a copy of the bill to send to the service. */
          /*-----------------------------------------------------------------*/
          var bill = {};
          bill.vendorID = $scope.invoice.vendorID;
          bill.number = $scope.invoice.number;
          bill.entryDate = $scope.invoice.entryDate;
          bill.memo = $scope.invoice.memo;
          bill.items = $scope.invoice.items;

          /*-----------------------*/
          /* Convert to billItems. */
          /*-----------------------*/
          angular.forEach(bill.items, function (item) {
            if (item.hasOwnProperty('inventoryProductID')) {
              item.inventoryID = item.inventoryProductID;
              delete item.inventoryProductID;
            }
          });

          /*-------------------------------------------------------*/
          /* Final sanity check. For some reason, some bills       */
          /* without a vendor ID and items are passing validation, */
          /* being sent to the server and generating errors.       */
          /*-------------------------------------------------------*/
          if (
            !bill.vendorID ||
            Tools.isEmpty(bill.memo) ||
            Tools.isEmpty(bill.number) ||
            (angular.isArray(bill.items) && bill.items.length < 1)
          ) {
            Alert.error('Invalid invoice.', 'Save Error');
            return;
          }

          /*---------------------------*/
          /* Call server to save bill. */
          /*---------------------------*/
          BusyScreen.setBusy(true);
          BooksSvc.saveBill(bill).then(
            function (resp) {
              BusyScreen.setBusy(false);
              InvoiceService.handleClosePOs(
                $scope.invoice.items,
                $scope.invoice.entryDate
              ).then(function () {
                /*-------------------------------------*/
                /* Resolve modal dialog with new bill. */
                /*-------------------------------------*/
                $uibModalInstance.close({
                  billID: ($scope.invoice.id = resp.data),
                  bill: $scope.invoice
                });
              });
            },
            function (resp) {
              Alert.error(resp.parsedResponse.message(), 'Save Error');
              BusyScreen.setBusy(false);
            }
          );
          /* The getOverdrawnItems dialog was rejected, return to application. */
        },
        function () {}
      );
    };
  };

  /***************************************************************************
   * show */
  /**
   * Display the invoice modal dialog to add an invoice to the current check.
   *
   * @param  vendor            Selected vendor to prepopulate 'For' field
   * @param  billItems         Bill items to automatically add to invoice.
   * @param  memo              Memo to use on invoice.
   * @param  restrictVendors   Flag indicating if the user should be prevented
   *                           from creating new vendors.
   * @return                   Promise to listen on
   ***************************************************************************/
  this.show = function (vendor, billItems, memo, restrictVendors) {
    return $uibModal.open({
      animation: true,
      backdrop: 'static',
      controller: AddInvoiceController,
      resolve: {
        restrictVendors: function () {
          return restrictVendors;
        },
        billItems: function () {
          return billItems;
        },
        memo: function () {
          return memo;
        },
        vendor: function () {
          return vendor;
        }
      },
      size: 'lg',
      template: addInvoiceDialogTemplate
    }).result;
  };
}
